import React, { useEffect } from 'react';
import { useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import DatePicker from '../Base/DatePicker/DatePicker';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import apiClient from '../../Services/ApiClient';
import * as yup from 'yup';
import RemoveButton from '../Button/RemoveButton';
import useNotification from '../../Hooks/useNotification.hook';
import { useEffectAsync } from '../../helpers';
import MultiSelect from '../Base/MultiSelect/MultiSelect';
import Select, { SelectValue } from '../Base/Select/Select';
import {
	useCreateWorkplaceMemberAbsenceMutation,
	useLazyNormalWorkplacesQuery,
	useLazyWorkplaceDoctorsQuery,
	useLazyWorkplaceMembersQuery,
} from '../../graphql/graphql';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';
import CheckboxField from '../Form/components/CheckboxField';
import RadioListField from '../Form/components/RadioListField';
import { useForm } from 'react-hook-form';
import FormWrapper from '../Form/FormWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import TimePickerField from '../Form/components/TimePickerField/TimePickerField';
import InputField from '../Form/components/InputField';
import FormCondition from '../Form/FormCondition';
import Loading from '../Loading';
import useTranslation from '../../Hooks/useTranslation';
import useRestApiClient from '../../Modules/Base/Hooks/useRestApiClient';
import { RadioListItem } from '../Form/components/RadioList';
import { getMomentDate } from '../../Utils/dates.util';

interface Props {
	selectedDate: any;
	onSuccess?: () => void;
	loggedUserId?: string;
	order?: any;
	params?: any;
}

interface Time {
	hours?: number;
	minutes?: number;
}

interface CalendarAbsenceEventValues {
	dateFrom: Moment;
	dateTo: Moment;
	timeFrom?: Moment;
	timeTo?: Moment;
	allowContactInAbsence: boolean;
	wholeDay: boolean;
	workplaceMember: SelectValue;
	workplaces: SelectValue[];
	reason: string;
}

const ALLOW_CONTACT_OPTIONS: RadioListItem[] = [
	{
		title: 'base.yes',
		value: true,
	},
	{
		title: 'base.no',
		value: false,
	},
];

const CalendarAbsenceEvent: React.FC<Props> = ({
	selectedDate,
	onSuccess,
	order,
}) => {
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [loggedUserWorkplacesIds, setLoggedUserWorkplacesIds] = useState<
		string[]
	>([]);
	const [selectedUserWorkplaces, setSelectedUserWorkplaces] = useState<
		SelectValue[]
	>([]);
	const [selectedWorkplacesIds, setSelectedWorkplacesIds] = useState<string[]>(
		[],
	);
	const [workplaceMembers, setWorkplaceMembers] = useState<SelectValue[]>([]);

	const [getWorkplaceMembers] = useLazyWorkplaceMembersQuery();
	const [getWorkplaces] = useLazyNormalWorkplacesQuery();
	const [getWorkplaceDoctors] = useLazyWorkplaceDoctorsQuery();
	const [createAbsence] = useCreateWorkplaceMemberAbsenceMutation();
	const { displayError, displayGQLError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();

	const validationSchema = yup.object({
		workplaceMember: yup
			.object({
				value: yup
					.string()
					.uuid()
					.required('validation.calendarAbsenceEvent.workplaceMember'),
				label: yup.string(),
			})
			.required('validation.calendarAbsenceEvent.workplaceMember'),
		workplaces: !order
			? yup
					.array()
					.of(
						yup.object({
							value: yup
								.string()
								.uuid()
								.required('validation.calendarAbsenceEvent.workplaces'),
							label: yup.string(),
						}),
					)
					.min(1)
					.required('validation.calendarAbsenceEvent.workplaces')
			: yup.mixed().notRequired(),

		dateFrom: yup.date().required('validation.calendarAbsenceEvent.dateFrom'),
		dateTo: yup.date().required('validation.calendarAbsenceEvent.dateTo'),
		timeFrom: yup.date().when('wholeDay', {
			is: false,
			then: yup
				.date()
				.typeError('validation.calendarAbsenceEvent.timeFrom')
				.required('validation.calendarAbsenceEvent.timeFrom'),
			otherwise: yup.date().notRequired(),
		}),
		timeTo: yup.date().when(['wholeDay', 'timeFrom'], {
			is: (wholeDay: boolean, timeFrom: Moment) => !wholeDay && !!timeFrom,
			then: yup
				.date()
				.typeError('validation.calendarAbsenceEvent.timeTo')
				.required('validation.calendarAbsenceEvent.timeTo')
				.test(
					'isAfterTimeFrom',
					'validation.calendarAbsenceEvent.timeToAfterTimeFrom',
					function (timeTo) {
						const { timeFrom } = this.parent;
						return !timeTo || !timeFrom || timeTo > timeFrom;
					},
				),
			otherwise: yup.date().notRequired(),
		}),
		reason: yup.string().required('validation.calendarAbsenceEvent.reason'),
		wholeDay: yup.boolean().required(),
		allowContactInAbsence: yup.boolean().required(),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	useEffectAsync(async () => {
		await handleLoadLoggedUserWorkplaces();
	}, []);

	useEffectAsync(async () => {
		await handleLoadWorkplaceMembers();
	}, [loggedUserWorkplacesIds]);

	useEffect(() => {
		setIsLoading(true);

		if (order) {
			formMethods.reset({
				dateFrom: moment(order.event.start),
				dateTo: moment(order.event.end),
				timeFrom: moment(order.event.start),
				timeTo: moment(order.event.end),
				workplaceMember: {
					value: order.event?.extendedProps?.doctor?.id,
					label: order.event?.extendedProps?.doctor?.name,
				},
				reason: order.event?.extendedProps?.note,
				allowContactInAbsence: order.event?.extendedProps.meta.find(
					(item: { key: string }) => item.key === 'allowContactInAbsence',
				).value,
				wholeDay: false,
			});
		} else {
			formMethods.reset({
				dateFrom: selectedDate,
				dateTo: selectedDate,
				allowContactInAbsence: false,
			});
		}

		setTimeout(() => {
			setIsLoading(false);
		}, 100);
	}, [order]);

	const handleLoadWorkplaceMembers = async () => {
		const tempWorkplaceMembers: SelectValue[] = [];

		const { data } = await getWorkplaceDoctors({
			workplaceIds: loggedUserWorkplacesIds,
			roleKeys: ['doctor', 'nurse', 'therapist'],
			take: 999,
		});

		(data?.workplaceDoctors?.data || []).forEach(workplaceDoctor => {
			const alreadyAdded = tempWorkplaceMembers.some(
				w => w.value === workplaceDoctor?.user?.id,
			);
			if (
				!alreadyAdded &&
				workplaceDoctor?.user?.id &&
				workplaceDoctor?.user?.profile?.fullName
			) {
				tempWorkplaceMembers.push({
					value: workplaceDoctor.user.id,
					label: workplaceDoctor.user.profile.fullName,
				});
			}
		});

		setWorkplaceMembers(tempWorkplaceMembers);
	};

	const handleLoadLoggedUserWorkplaces = async () => {
		const tempWorkplacesIds: string[] = [];

		const { data } = await getWorkplaces();

		(data?.workplaces?.data || []).forEach(workplace => {
			if (workplace) {
				tempWorkplacesIds.push(workplace.id);
			}
		});

		setLoggedUserWorkplacesIds(tempWorkplacesIds);
	};

	const handleSelectWorkplaceMember = async (value: SelectValue) => {
		const tempWorkplaces: SelectValue[] = [];

		const { data } = await getWorkplaceMembers({
			userId: value.value as string,
			workplaceIds: loggedUserWorkplacesIds,
		});

		(data?.workplaceMembers?.data || []).forEach(workplace => {
			if (workplace && workplace.workplace?.doctorDisplayName) {
				tempWorkplaces.push({
					value: workplace.id,
					label: workplace.workplace.doctorDisplayName,
				});
			}
		});

		setSelectedUserWorkplaces(tempWorkplaces);
	};

	const handleSelectWorkplaces = async (values: SelectValue[]) => {
		const tempWorkplacesIds: string[] = [];

		for (const workplace of values || []) {
			if (typeof workplace?.value === 'string') {
				tempWorkplacesIds.push(workplace.value);
			}
		}
		setSelectedWorkplacesIds(tempWorkplacesIds);
	};

	const handleCreate = async (vals: any) => {
		setIsLoading(true);
		const values: CalendarAbsenceEventValues = vals;

		const tFrom = getMomentDate(values.timeFrom);
		const tTo = getMomentDate(values.timeTo);

		const timeFrom: Time = values.wholeDay
			? { hours: 0, minutes: 0 }
			: {
					hours: tFrom.hours(),
					minutes: tFrom.minutes(),
			  };

		const timeTo: Time = values.wholeDay
			? { hours: 23, minutes: 59 }
			: {
					hours: tTo.hours(),
					minutes: tTo.minutes(),
			  };

		const { dateFrom, dateTo, reason, allowContactInAbsence } = values;

		try {
			const input = {
				workplaceMemberIds: selectedWorkplacesIds,
				start: moment(dateFrom)
					.clone()
					.startOf('day')
					.add(timeFrom.hours, 'hours')
					.add(timeFrom.minutes, 'minutes'),
				end: moment(dateTo)
					.clone()
					.startOf('day')
					.add(timeTo.hours, 'hours')
					.add(timeTo.minutes, 'minutes'),
				description: reason,
				allowContactInAbsence,
			};

			const response = await createAbsence({ input });

			displayGQLError(response);
			if ('data' in response) {
				showNotificationSuccess({
					content: 'base.absenceCreated',
				});

				onSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleRemove = async function () {
		await apiClient.delete('/events/cancel/' + order.event.id);
		onSuccess && onSuccess();
	};

	const handleUpdate = async (vals: any) => {
		const values: CalendarAbsenceEventValues = vals;

		const { dateFrom, dateTo, workplaceMember, reason, allowContactInAbsence } =
			values;

		const timeFrom = getMomentDate(values.timeFrom);
		const timeTo = getMomentDate(values.timeTo);

		const response = await apiClient.patch('/event/' + order.event.id, {
			dateFrom: moment(dateFrom)
				.clone()
				.startOf('day')
				.add(timeFrom?.hours(), 'hours')
				.add(timeFrom?.minutes(), 'minutes'),
			dateTo: moment(dateTo)
				.clone()
				.startOf('day')
				.add(timeTo?.hours(), 'hours')
				.add(timeTo?.minutes(), 'minutes'),
			eventMembers: [
				{
					workplaceMemberId: workplaceMember.value,
				},
			],
			description: reason,
			allowContactInAbsence,
		});

		if (response.status === 200) {
			showNotificationSuccess({
				content: 'base.absenceUpdated',
			});
			onSuccess?.();
		} else {
			showUnexpectedError();
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<div>
				<FormControl
					label='base.selectWorker'
					name='workplaceMember'
					onChange={handleSelectWorkplaceMember}
					isRequired={true}>
					<Select
						options={workplaceMembers}
						placeholder={t('base.shared.pick')}
						isSearchable={true}
					/>
				</FormControl>
				{!order && (
					<>
						<FormControl
							label='base.chooseWorkplace'
							name='workplaces'
							onChange={handleSelectWorkplaces}
							isRequired={true}>
							<MultiSelect
								options={selectedUserWorkplaces}
								isSearchable={true}
								placeholder={t('base.chooseWorkplace')}
								noOptionsMessage={t('base.pickWorkerFist')}
							/>
						</FormControl>
						<CheckboxField name='wholeDay' tx='base.wholeDay' isWrapped />
					</>
				)}
				<div className={'row'}>
					<div className={'col-6'}>
						<FormControl
							label='base.shared.dateFrom'
							name='dateFrom'
							isRequired={true}>
							<DatePicker />
						</FormControl>
						<FormControl
							label='base.shared.dateTo'
							name='dateTo'
							isRequired={true}>
							<DatePicker />
						</FormControl>
					</div>
					<div className={'col-6'}>
						<FormCondition controlName='wholeDay' equal={false}>
							<TimePickerField
								name='timeFrom'
								label='base.shared.timeFrom'
								isRequired
								isWrapped
							/>
							<TimePickerField
								name='timeTo'
								label='base.shared.timeTo'
								isRequired
								isWrapped
							/>
						</FormCondition>
					</div>
				</div>
				<InputField
					label='component.calendar.absence.reason'
					name='reason'
					isRequired
					isWrapped
				/>
				<RadioListField
					label='component.calendar.absence.allowContactInAbsence'
					name='allowContactInAbsence'
					data={ALLOW_CONTACT_OPTIONS}
					isWrapped
					isRequired
					inLine
				/>
				<div
					className={'d-flex flex-row align-items-center mt-4'}
					style={{ gap: '15px' }}>
					<SubmitButton
						onSubmit={order ? handleUpdate : handleCreate}
						title={order ? 'base.shared.edit' : 'base.create'}
					/>
					<RemoveButton onClick={handleRemove} />
				</div>
			</div>
		</FormWrapper>
	);
};

export default CalendarAbsenceEvent;
